import TagsCell from "src/components/TagsCell";
import { TableColumnsType, Tag } from "antd";
import dayjs from "dayjs";
import { CLAIMS } from "src/constants";
import { DataColumnConfig } from "src/hooks/useTransformDataColumns";
import { Product, ProviderValues } from "src/models/ProductModel";
import { ellipsis } from "src/utility/utility";
import EditModal from "./EditModal";

type DataType = Record<string, any>;

export type ProductWithActions = Product & {
  actions?: void;
};

export const config: DataColumnConfig<ProductWithActions> = {
  merchantNameEn: { searchType: "text" },
  merchantNameAr: { searchType: "text" },
  offerNameEn: { searchType: "text" },
  offerNameAr: { searchType: "text" },
  offerId: { searchType: "text" },
  provider: { searchType: "text" },
  projectValue: {
    searchType: "tree",
    options: ProviderValues,
  },
  categoryId: {
    searchType: "text",
  },
  actions: {
    claims: CLAIMS.EDIT,
  },
};

export const columns: TableColumnsType<Product & { actions?: void }> = [
  {
    title: "Offer ID",
    key: "offerId",
    dataIndex: "offerId",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Number",
    key: "offerNumber",
    dataIndex: "offerNumber",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Is Frontend",
    key: "isFrontend",
    dataIndex: "isFrontend",
    ellipsis: true,
    render: (value) => (Boolean(value) ? "Yes" : "No"),
  },
  {
    title: "Provider",
    key: "provider",
    dataIndex: "provider",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Project Value",
    key: "projectValue",
    dataIndex: "projectValue",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Type",
    key: "offerType",
    dataIndex: "offerType",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Name En",
    key: "offerNameEn",
    dataIndex: "offerNameEn",
    ellipsis: true,
    sorter: (a: DataType, b: DataType) =>
      a.offerName.localeCompare(b.offerName),
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Name Ar",
    key: "offerNameAr",
    dataIndex: "offerNameAr",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Merchant Name En",
    key: "merchantNameEn",
    dataIndex: "merchantNameEn",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Merchant Name Ar",
    key: "merchantNameAr",
    dataIndex: "merchantNameAr",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Merchant ID",
    key: "merchantId",
    dataIndex: "merchantId",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Tags",
    key: "offerTags",
    dataIndex: "offerTags",
    ellipsis: true,
    render: (tags: Product["offerTags"]) => <TagsCell tags={tags} />,
  },
  {
    title: "Sorting Number",
    key: "sortingNumber",
    dataIndex: "sortingNumber",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Category ID",
    key: "categoryId",
    dataIndex: "categoryId",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Description",
    key: "offerDesc",
    dataIndex: "offerDesc",
    ellipsis: true,
    render: (value) => <span>{value ? ellipsis(value, 30) : "_"}</span>,
  },
  {
    title: "Offer Status",
    key: "offerStatus",
    dataIndex: "offerStatus",
    ellipsis: true,
    render: (value: string) => {
      return <Tag>{value}</Tag>;
    },
  },
  {
    title: "Offer Status ID",
    key: "offerStatusId",
    dataIndex: "offerStatusId",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Type ID",
    key: "offerTypeId",
    dataIndex: "offerTypeId",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Conversion Type",
    key: "conversionType",
    dataIndex: "conversionType",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Tag Line",
    key: "tagLine",
    dataIndex: "tagLine",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Discount",
    key: "discount",
    dataIndex: "discount",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Start Date",
    key: "startDate",
    dataIndex: "startDate",
    ellipsis: true,
    render: (value) => (
      <span>{value ? dayjs(value).format("DD-MM-YYYY") : "_"}</span>
    ),
  },
  {
    title: "End Date",
    key: "endDate",
    dataIndex: "endDate",
    ellipsis: true,
    render: (value) => (
      <span>{value ? dayjs(value).format("DD-MM-YYYY") : "_"}</span>
    ),
  },
  {
    title: "Expire Date",
    key: "expireDate",
    dataIndex: "expireDate",
    ellipsis: true,
    render: (value) => (
      <span>{value ? dayjs(value).format("DD-MM-YYYY") : "_"}</span>
    ),
  },
  {
    title: "Original Price",
    key: "originalPrice",
    dataIndex: "originalPrice",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Discount Percentage",
    key: "discountPercentage",
    dataIndex: "discountPercentage",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Is Favourite",
    key: "isFavourite",
    dataIndex: "isFavourite",
    ellipsis: true,
    render: (value) => <span>{value ? "Yes" : "No"}</span>,
  },
  {
    title: "Discount Type",
    key: "discountType",
    dataIndex: "discountType",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Transaction Count",
    key: "transactionCount",
    dataIndex: "transactionCount",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Offer Category",
    key: "offerCategory",
    dataIndex: "offerCategory",
    ellipsis: true,
    render: (value) => (
      <span>{value?.categoryName ? value.categoryName : "_"}</span>
    ),
  },
  {
    title: "Merchant Category",
    key: "merchantCategory",
    dataIndex: "merchantCategory",
    ellipsis: true,
    render: (value) => (
      <span>{value?.categoryName ? value.categoryName : "_"}</span>
    ),
  },
  {
    title: "Offer Max Discount",
    key: "offerMaxDiscount",
    dataIndex: "offerMaxDiscount",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Voucher Validity In Months",
    key: "voucherValidityInMonths",
    dataIndex: "voucherValidityInMonths",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Voucher Validity In Days",
    key: "voucherValidityInDays",
    dataIndex: "voucherValidityInDays",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    ellipsis: true,
    className: "bg-white",
    render: (record: any) => <EditModal record={record} />,
  },
];
